// ===== Types you can share app-wide =====
export type PlanTier = 'Good' | 'Better' | 'Best' | 'Listing';

export enum ClaimStatusKeys {
  pill = 'PILL',
  header = 'HEADER',
  subHeader = 'SUB_HEADER',
  nextSteps = 'NEXT_STEP',
  claimSummary = 'CLAIM_SUMMARY',
}

export interface PaymentHistoryItem {
  date: string; // ISO string (planSalesDate or certificateStartDate)
  amount: number; // premium.gross OR sum of salesPrice for that date (add-ons)
  planType: 'Plan' | 'Add-Ons';
}

export interface PlanDetail extends ApiPlanResponse {
  planType: PlanTier; // Derived from planCode prefix
  totalLiabilityLimit: number; // From CORE-* certificate.product.liabilityLimit
  deviceTypeCodes: string[]; // Unique union from CORE allowedDeviceTypes + all equipments[].deviceTypeCode
  coverageTypeCodes: string[]; // List of coverageTypeCode from all certificates
  paymentHistory: PaymentHistoryItem[]; // See rules below
  deductible: number; // From CORE-* MB Coverage deductible amount
  coreCertificate: string; // Certificate number of the CORE-* certificate
  nonCoreCertificate: string; // Certificate number of the NON-CORE-* certificate (For Lowes)
  equipments: Equipment[];
  isAddonsAllowed: boolean;
  planStartDate: string;
  planEndDate: string;
}

// ===== Types that mirror your API payload (trim as needed) =====
export interface ApiPlanResponse {
  planNumber: string;
  planCode: string;
  planSalesDate: string;
  planTerm: number;
  dealerCode: string;
  status: string;
  // Lowes' bulkplandetails response uses `activeClaims`; AHW's uses `activeServiceRequests`
  // instead — same open-count concept, different wire field name per client's upstream. Both
  // are optional here since only one is ever present on a given response; PlanService.buildPlanDetail
  // normalizes them into a single `activeClaims` value so every downstream consumer stays unchanged.
  activeClaims?: number | null;
  activeServiceRequests?: number | null;
  premium: { gross: number; net: number; tax: number };
  certificates: Certificate[];
  claims: Claim[];
  isMultiCertPolicy: boolean;
}

// TODO: MAPP + LPP Manufacturer Warranty — confirm with BE team before wiring up IProductWarranty on MAPP products.
// Both MAPP and LPP warranty will be wired at the same time. See also: plan-coverage-single-cert-policy.mock-data.ts.
// Findings: Equipment has no warranty field. Two candidates on Certificate:
//   1. coverages[]: if the API returns a Coverage entry for manufacturer warranty, beginDate/endDate
//      would represent the warranty window. Need to confirm what coverageTypeCode value they use
//      (e.g. 'MFG_WARRANTY', 'MANUFACTURER', etc.) so we can filter by it.
//   2. certificateReferenceData: any[] — catch-all array that may carry extended/custom fields
//      including warranty details (brand contact URL, description, active flag).
// Questions for BE:
//   - Is manufacturer warranty exposed at all in the plan detail API response for MAPP?
//   - If via coverages[], what is the coverageTypeCode for manufacturer warranty?
//   - Does that Coverage entry's beginDate/endDate represent the warranty window?
//   - If via certificateReferenceData[], what is the shape of the warranty entry (name/value pairs)?
//   - Is warranty per-equipment or per-certificate?
// Once confirmed: add warranty field to Equipment or Certificate (whichever BE uses),
// map to IProductWarranty in plan-coverage-mapp.component.ts products computed,
// and pass through in onProductClick nav state (field already exists on IProductDetailNavState).
export interface Certificate {
  certificateNumber: string;
  certificateSalesDate: string;
  status: string;
  salesPrice: number;
  premium: { gross: number };
  product: {
    productCode: string;
    productDescription: string;
    allowRegisteredItems: boolean;
    liabilityLimit: number;
    allowedDeviceTypes: string[];
  };
  equipments: Equipment[];
  coverages: Coverage[];
  certificateReferenceData: any[];
}

export interface Claim {
  certificate: ClaimCertificate;
  claimNumber: string;
  status: string;
  lossInfo: LossInfo;
  claimedEquipment: Equipment;
  actionsSummary: ClaimActionSnapshot;
  // Optional: a freshly-filed claim (e.g. "Approved" with no fulfillment ticket yet) can come
  // back with no `fulfillment` block at all — every consumer already reads it via `?.`.
  fulfillment?: FulfillmentSnapshot;
  properties: ExtendedProperty[];
}

// ===== bulkplandetails endpoint (bulk fetch) =====
export interface BulkPlanDetailsResponse {
  plans: {
    count: number;
    data: ApiPlanResponse[];
  };
  claims: BulkClaim[];
}

// bulkplandetails root-level claim — mapped into Claim by PlanService.mapBulkClaimsToClaims.
// The live wire uses the Claim field names (actionsSummary.actionsCount,
// fulfillment.fulfillmentTypeEnum); count / fulfillmentTypeCode are legacy fallbacks.
export interface BulkClaim {
  claimNumber: string;
  planNumber: string;
  status: string;
  statusDate?: string;
  caseNumber?: string;
  certificate: ClaimCertificate;
  actionsSummary: { actionsCount?: number; count?: number; lastActionCode?: string };
  fulfillment?: {
    fulfillmentType: string;
    fulfillmentTypeEnum?: string;
    fulfillmentTypeCode?: string;
    typeCode: string;
    statusDate: string;
    statusReason: string;
    entityType: string;
  };
  claimedEquipment?: Equipment;
  lossInfo: { dateOfLoss?: string; coverageTypeCode: string; reportedDate?: string; problemDescription?: string; currentProblemDescription?: string };
  properties?: ExtendedProperty[];
  deductible?: { gross: number; tax: number; net: number };
  fees?: { nonReturnFee?: { gross: number } };
}

export interface LossInfo {
  coverageTypeCode: string;
  // Wire values, not real Date objects — the API sends "9/11/2026 12:00:00 AM"-style strings
  // and nothing in the app parses them, so the type now matches what actually flows through.
  dateOfLoss?: string;
  reportedDate?: string;
  problemDescription: string;
}

export interface ClaimCertificate {
  certificateNumber: string;
  status: string;
  // Bulk/multi-policy clients: the owning policy's plan number, used to reverse-lookup which
  // plan in $planDetails this claim actually belongs to (a claims union can span policies).
  masterCertificateNumber?: string;
}

export interface ClaimActionSnapshot {
  actionsCount: number;
  lastActionCode: string;
}

export interface FulfillmentSnapshot {
  fulfillmentType: string;
  fulfillmentTypeEnum: string;
  typeCode: string;
  entityType: string;
  statusDate: string;
  statusReason: string;
}

export interface ExtendedProperty {
  name: string;
  value: string;
}

export interface Equipment {
  deviceId: string;
  registrationName?: string;
  deviceTypeCode?: string;
  manufacturerName?: string;
  model?: string;
  description?: string;
  registrationStatus?: string;
  childCertificateNumber?: string;
  serialNumber?: string;
  imeiNumber?: string;
  dealerSkuNumber?: string;
  colorCode?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  categoryCode?: string;
  subCategoryCode?: string;
  memoryCode?: string;
  carrierCode?: string;
  registeredItemName?: string;
  customerIdentification?: string;
  registrationCondition?: string;
}

export interface Coverage {
  coverageTypeCode: string;
  beginDate: string;
  endDate: string;
  allowClaims: boolean;
  deductibles: DeductibleData[];
  // Device types this coverage does NOT extend to even though the certificate covers them
  // (e.g. FL food-spoilage excludes TRCP trash compactors). Absent/empty means no exclusions.
  excludeDeviceTypes?: string[];
}

/**
 * Some equipment records come back from the API with the literal sentinel string "UNKNOWN"
 * as manufacturerName instead of null/empty when the source system never captured a real
 * brand (confirmed via API trace, not inserted anywhere in this app). Treat it the same as
 * missing so "is brand known?" checks, card titles, and the product-info form's
 * readonly-lock don't mistake this placeholder for real data.
 */
export function resolveKnownBrand(manufacturerName: string | null | undefined): string {
  const trimmed = manufacturerName?.trim() ?? '';
  return trimmed.toUpperCase() === 'UNKNOWN' ? '' : trimmed;
}

export interface EquipmentRequest {
  deviceId?: string;
  // registered-equipment/update expects the identifier under this key instead of deviceId —
  // see RegisteredEquipmentService.updateEquipment's caller (plan-coverage-product-detail
  // .component.ts's openApplianceDetailsDialog).
  equipmentId?: string;
  registrationName: string;
  equipmentTypeCode: string;
  manufacturerName?: string;
  model?: string;
  description?: string;
  marketingName?: string;

  certificateIdentifier?: string;
  planNumber?: string;
  dealerCode?: string;
  isCore?: boolean;

  serialNumber?: string;
  equipmentSalesDate?: string;
  equipmentSalesPrice?: number;
}

export interface DeductibleData {
  deductibleBasedOnCode: string;
  deductible: number;
  methodOfRepairCode: string;
  deductibleSource: string;
}
